(function () {
	'use strict';

	const resolve = x => ({
		on: () => resolve(x),
		otherwise: () => x
	});

	/**
	 * Functional switch case utility
	 *
	 * Add predicate functions to check if value is "truthie" it will
	 * proceed calling the second function otherwise it will proceed to
	 * the next `.on` until it matches or hits the `.otherwise case`
	 *
	 * Example:
	 *
	 * match(value)
	 * 	.on(predicateFn, matchFn)
	 * 	.on(otherPredicateFn, otherMatchFn)
	 * 	.otherwise(onNoMatchesFn)
	 */
	const match = x => ({
		on: (cond, fn) => (cond(x) ? resolve(fn(x)) : match(x)),
		otherwise: fn => fn(x)
	});

	/**
	 * Safe getter for objects
	 *
	 * Innermost value is returned when full path exist,
	 * but if the path is broken null will be returned.
	 *
	 * @example
	 * 		getByPath(['keyA', 'keyB'], { keyA: { keyB: 0 } })
	 * 		// returns 0
	 *
	 * 		getByPath(['keyA', 'keyB'], { keyA: { keyB: 'Value' } })
	 * 		// returns 'Value'
	 *
	 * 		getByPath(['keyA', 'keyB', 'keyC'], { keyA: {keyB: undefined } })
	 * 		// returns null
	 *
	 * @param {array} p Path
	 * @param {object} o Object
	 * @return {*}
	 */
	const getByPath = (p, o) => {
		const innerMostPath = p.slice(-1)[0];
		return p.reduce(
			(xs, x) =>
				(xs && xs[x] !== undefined && x === innerMostPath) || (xs && xs[x]) ? xs[x] : null,
			o
		);
	};

	const groupsOf = (array, sliceSize) => {
		const sliced = array.reduce(
			(acc, x) => {
				const last = () => acc.slice(-1)[0];
				if (last().length >= sliceSize) {
					acc.push([]);
				}
				last().push(x);
				return acc;
			},
			[[]]
		);

		return sliced.filter(x => x.length > 0);
	};

	const getCurrentUnixTimeStamp = ({ format }) => {
		const msTimeStamp = () => new Date().getTime();
		const secTimeStamp = () => Math.round(msTimeStamp() / 1000);

		return match(format)
			.on(x => ['sec', 'seconds'].includes(x), secTimeStamp)
			.on(x => ['ms', 'milliseconds'].includes(x), msTimeStamp)
			.otherwise(msTimeStamp);
	};

	/**
	 * First we use encodeURIComponent to get percent-encoded UTF-8, then we convert
	 * the percent encodings into raw bytes which can be fed into btoa.
	 *
	 * @param btoa
	 * @param str
	 * @returns {string}
	 */
	const b64EncodeUnicode = (btoa, str) => {
		const toSolidBytes = (match, p1) => String.fromCharCode('0x' + p1);
		return btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, toSolidBytes));
	};

	const csvDownloadStream = btoa => csvString =>
		'data:application/octet-stream;base64,' + b64EncodeUnicode(btoa, csvString);

	const xlsxDownloadStream = btoa => xlsxString =>
		'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' +
		b64EncodeUnicode(btoa, xlsxString);

	module.exports = {
		getByPath: getByPath,
		match: match,
		groupsOf: groupsOf,
		csvDownloadStream: csvDownloadStream,
		xlsxDownloadStream: xlsxDownloadStream,
		getCurrentUnixTimeStamp: getCurrentUnixTimeStamp
	};
})(typeof module !== 'undefined' && module.exports);
